import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import { Form, Formik, FormikProps } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import useEnv from "../../../../../hooks/env";
import ProductReferenceResponse from "../../../../../api/models/ProductReferenceResponse";
import {
  BaseProductsSearchAutocomplete,
  NartsSearchAutocomplete,
  ProductNamesSearchAutocomplete,
} from "../../../../../components/Autocomplete/CustomSearchAutoComplete.instances";
import CustomButton from "../../../../../components/Button/CustomButton";
import ProductApi from "../../../../../api/services/ProductApi";
import NartResponse from "../../../../../api/models/product/NartResponse";
import ProductNameResponse from "../../../../../api/models/product/ProductNameResponse";
import BaseProductResponse from "../../../../../api/models/product/BaseProductResponse";
import ErrorSnackbarContext from "../../../../../contexts/ErrorSnackbarContext";
import useStyles from "./SuggestionFormStyle";
import {
  initialFormValues,
  MIN_NART_CHARACTERS,
  ProductSuggestionFormProps,
  ProductSuggestionInnerFormikProps,
} from "./SuggestionForm";

const SuggestionFormRequester: React.FC<ProductSuggestionFormProps> = ({ handleSubmit, preset }) => {
  const { api } = useEnv();
  const productApi = new ProductApi(api);

  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const formRef = useRef<FormikProps<ProductSuggestionInnerFormikProps>>(null);

  const autocompleteNartRef = useRef<any>(null);
  const autocompleteProductNameRef = useRef<any>(null);
  const autocompleteBaseProductRef = useRef<any>(null);

  const handleReset = () => {
    if (autocompleteNartRef.current && autocompleteNartRef.current.reset) {
      autocompleteNartRef.current.reset(false);
    }
    if (autocompleteProductNameRef.current && autocompleteProductNameRef.current.reset) {
      autocompleteProductNameRef.current.reset(false);
    }
    if (autocompleteBaseProductRef.current && autocompleteBaseProductRef.current.reset) {
      autocompleteBaseProductRef.current.reset(false);
    }
  };

  const [disabledNartField, setDisabledNartField] = useState(false);
  const [disableEditing, setDisableEditing] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    const nartValue = formRef?.current?.values.nartField.nart;
    if (nartValue && disabledNartField) {
      productApi
        .getProductWithPropsByNart(nartValue)
        .then(({ data }) => {
          showSnackbar("Product properties received", "success");
          formRef.current?.setFieldValue("productNameField", {
            code: data.productName!,
            description: data.productNameDescription!,
          });
          formRef.current?.setFieldValue("baseProductField", {
            code: data.baseProduct!,
            description: data.baseProductDescription!,
          });
        })
        .catch(handleHttpError);
    }
  }, [disabledNartField]);

  const createInitialValues = () => {
    return {
      nartField: { nart: preset?.nart || "" },
      productNameField: { code: preset?.productName || "", description: preset?.productName || "" },
      baseProductField: { code: preset?.baseProduct || "", description: preset?.baseProduct || "" },
    };
  };

  const handleOnSubmit = (values: ProductSuggestionInnerFormikProps) => {
    const { productNameField, baseProductField, nartField } = values;

    handleSubmit({
      material: null,
      nart: nartField.nart,
      productName: productNameField.code,
      baseProduct: baseProductField.code,
    });
  };

  const renderDeprecatedProductInformation = (productRefResponse?: ProductReferenceResponse | null) => {
    if (productRefResponse?.name && productRefResponse?.nart) {
      return (
        <span className={classes.nartSearchHelperText}>
          {`Previously saved product using deprecated Blox API: 
          ${productRefResponse.nart} - ${productRefResponse.name}`}
        </span>
      );
    }
  };

  return (
    <Formik<ProductSuggestionInnerFormikProps>
      innerRef={formRef}
      initialValues={createInitialValues()}
      onSubmit={handleOnSubmit}
    >
      {(formikProps: FormikProps<ProductSuggestionInnerFormikProps>): JSX.Element => {
        const { setFieldValue, values } = formikProps;
        return (
          <Form>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              <Grid item xs={12}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span className={classes.nartSearchHelperText}>
                    Enter a NART that is at least 3 characters long to perform a search.
                  </span>
                  {renderDeprecatedProductInformation(preset)}
                </div>
                <NartsSearchAutocomplete
                  autoFocus
                  disabled={
                    (Boolean(values.productNameField.code) || Boolean(values.baseProductField.code)) &&
                    !values.nartField.nart
                  }
                  ref={autocompleteNartRef}
                  inputLabel="NART"
                  onChange={(e, value): void => {
                    if (value) {
                      setFieldValue("nartField", value);
                      setDisableEditing(true);
                      setDisabledNartField(true);
                    } else {
                      handleReset();
                      setDisableEditing(false);
                      setDisabledNartField(false);
                    }
                  }}
                  optionFetch={(searchTerm): Promise<{ totalPages: number; data: NartResponse[] }> => {
                    if (searchTerm.length > MIN_NART_CHARACTERS) {
                      return productApi.getNarts(searchTerm).then((response) => ({
                        totalPages: 1,
                        data: response.data,
                      }));
                    }
                    return Promise.resolve({
                      totalPages: 1,
                      data: [],
                    });
                  }}
                  map={(value): { key: string; label: string } => ({
                    key: value.nart,
                    label: value.nart,
                  })}
                  value={values.nartField}
                />
              </Grid>
              <Grid item xs={12}>
                <ProductNamesSearchAutocomplete
                  ref={autocompleteProductNameRef}
                  disabled={disableEditing}
                  inputLabel="Product name"
                  onChange={(e, value): void => {
                    if (value) {
                      setFieldValue("productNameField", value);
                      setDisableEditing(false);
                    } else {
                      handleReset();
                    }
                  }}
                  optionFetch={(searchTerm): Promise<{ totalPages: number; data: ProductNameResponse[] }> => {
                    return productApi.getProductNames(searchTerm).then((response) => ({
                      totalPages: 1,
                      data: response.data,
                    }));
                  }}
                  map={(value): { key: string; label: string } => ({
                    key: value.code,
                    label: value.code,
                  })}
                  value={values.productNameField}
                />
              </Grid>
              <Grid item xs={12}>
                <BaseProductsSearchAutocomplete
                  ref={autocompleteBaseProductRef}
                  disabled={disableEditing}
                  inputLabel="Base product"
                  onChange={(e, value): void => {
                    if (value) {
                      setFieldValue("baseProductField", value);
                      setDisableEditing(false);
                    } else {
                      handleReset();
                    }
                  }}
                  optionFetch={(searchTerm): Promise<{ totalPages: number; data: BaseProductResponse[] }> => {
                    return productApi.getBaseProducts(searchTerm).then((response) => ({
                      totalPages: 1,
                      data: response.data,
                    }));
                  }}
                  map={(value): { key: string; label: string } => ({
                    key: value.code,
                    label: value.code,
                  })}
                  value={values.baseProductField}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" marginTop="26px">
              <CustomButton
                type="submit"
                text="Add Product"
                primary
                disabled={
                  !values?.nartField?.nart && !values?.productNameField?.code && !values?.baseProductField?.code
                }
              />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SuggestionFormRequester;
